<svelte:options customElement={{ tag: 'bt-curation-content', shadow: 'none' }} />

<script lang="ts">
  // @ts-ignore
  import Sortable from 'sortablejs';
  import { onMount } from 'svelte';
  import { eventBus } from '$shared/lib/eventBus';
  import { toCompactDuration } from '$lib/utils';

  import { type CuratedItem } from './curationTypes';
  import CurationSection from './CurationSection.svelte';
  import CurationListItem from './CurationListItem.svelte';
  import CurationQuestionSet from './CurationQuestionSet.svelte';

  export let contents: string;
  let contentsJSON: CuratedItem[] = JSON.parse(contents);
  export let curationId = 0;
  export let editable = contentsJSON[0]?.editable;

  export let addModalText;
  export let addToCurationText;
  export let contentTypeText;
  export let descriptionLabel;
  export let dropdownArticle;
  export let dropdownAudio;
  export let dropdownLink;
  export let dropdownSurvey;
  export let dropdownText;
  export let dropdownVideo;
  export let editModalText;
  export let estimatedDurationLabel;
  export let itemDeleteConfirmationText = 'Are you sure you want to delete this item?';
  export let optionalText;
  export let learningObjectivesHeader = 'Learning Objectives';
  export let linkText;
  export let pasteUrlHereText;
  export let questionSetAddOptionText;
  export let questionSetAddQuestionText;
  export let questionSetBestPracticesText;
  export let questionSetDeleteLabel;
  export let questionSetDeleteOptionConfirmText;
  export let questionSetDeleteOptionLabel;
  export let questionSetDeleteQuestionLabel;
  export let questionSetDescriptionPlaceholder;
  export let questionSetDuplicateLabel;
  export let questionSetDuplicateQuestionLabel;
  export let questionSetDurationPlaceholder;
  export let questionSetEmptyStateText;
  export let questionSetHelpCenterLinkText;
  export let questionSetMinutesText;
  export let questionSetMoveDownLabel;
  export let questionSetMoveLabel;
  export let questionSetMoveQuestionLabel;
  export let questionSetMoveToBottomLabel;
  export let questionSetMoveToTopLabel;
  export let questionSetMoveUpLabel;
  export let questionSetMultipleChoiceText;
  export let questionSetOpenEndedText;
  export let questionSetOptionPlaceholder;
  export let questionSetQuestionPlaceholder;
  export let questionSetResponsePreviewText;
  export let questionSetSaveAndCloseText;
  export let questionSetSavedText;
  export let questionSetTitlePlaceholder;
  export let requiredText;
  export let sectionDescriptionPlaceholder = 'Click to edit section description';
  export let saveChangesText;
  export let sectionDragPlaceholder = 'Drag and drop content here.';
  export let sectionDurationLabel = 'Duration';
  export let sectionTitlePlaceholder = 'Click to edit section name';
  export let sectionActionsMoveUpLabel = 'Move Section Up';
  export let sectionActionsMoveDownLabel = 'Move Section Down';
  export let sectionActionsDeleteLabel = 'Delete Section';
  export let sectionDeleteConfirmationText = 'Are you sure you want to delete this section, including the items within it?';
  export let titleLabel;
  export let titlePlaceholder;
  export let validUrlText;

  let contentsList: HTMLDivElement;
  let currentElement: any;
  let parentSortable: any;

  onMount(async function () {
    parentSortable = Sortable.create(contentsList, {
      group: {
        name: 'contentsList',
        put: true,
        dataIdAttr: 'data-id',
        pull: function () {
          // only items with level 0 can be dragged out of the list
          var level = currentElement.getAttribute('data-level');
          if (level == 1) {
            return false;
          } else {
            return true;
          }
        }
      },
      scroll: true,
      filter: '.addnew', // prevent Add New bar from being sorted
      animation: 200,
      handle: '.handle',
      // Element dragging ended
      onEnd: function (/**Event*/ evt: any) {
        let itemEl = evt.item.getAttribute('data-id'); // dragged HTMLElement
        let fromCuration = evt.from.getAttribute('data-id');
        let toCuration = evt.to.getAttribute('data-curatable-id');
        let newIndex = evt.newIndex; // element's new index within new parent
        handleSort(itemEl, newIndex, fromCuration, toCuration);
      },
      onStart: function (evt: any) {
        currentElement = evt.item;
        parentSortable = parentSortable;
      },
      onChange: function () {
        parentSortable = parentSortable;
      },
    });
  });

  function handleSort(
    itemEl: number,
    newIndex: number,
    fromCuration: number,
    toCuration: number
  ) {
    fetch(`/curations/${fromCuration}/curated_items/${itemEl}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      // @ts-ignore
      headers: {
        'Content-Type': 'application/json',
        // @ts-ignore
        'X-CSRF-Token': document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute('content')
      },
      body: JSON.stringify({
        position: newIndex,
        to_curation: toCuration
      })
    });
  }

  onMount(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const scrollY: any = urlParams.get('scrollTo') || 0;
    if (scrollY !== 0) {
      scrollTo(0, scrollY);
    }
  })

  onMount(() => {
    const addedHandler = (newItem: any) => {
      // An empty curation has no first item to read editable from on page load, so take it from the new item.
      editable = editable ?? newItem.editable;
      contentsJSON = [
        JSON.parse(JSON.stringify(newItem)),
        ...contentsJSON.map(item => JSON.parse(JSON.stringify(item)))
      ];
    };

    // Handles “external-link-updated” events;
    // Finds the matching item (top-level or nested), merges updated fields, and keeps old values as fallback.
    const updatedHandler = (updated: any) => {
      // stable link id; fall back to whatever the emitter sent as `id`.
      const targetCuratableId = updated.curatable_id ?? updated.id;

       // Merge updates into a single row object.
      const patch = (prev: any) => {
        const nextUrl = updated.url ?? prev.url;
        const rawDuration = updated.duration ?? updated.time_overlay ?? prev.duration;
        const nextDuration = typeof rawDuration === "number" ? toCompactDuration(rawDuration) : rawDuration;

        return {
          ...prev,
          title: updated.title ?? prev.title,
          url: nextUrl,
          externalUrl: nextUrl,
          thumbnail_url: updated.thumbnail_url ?? prev.thumbnail_url,
          type_overlay: updated.type_overlay ?? updated.content_type ?? prev.type_overlay,
          duration: nextDuration,
          subeyebrow: updated.subeyebrow ?? updated.expert_title ?? prev.subeyebrow,
        };
      };

      let found = false;

      contentsJSON = contentsJSON.map((entry: any) => {
        // Top-level item
        if (!entry.is_curation) {
          if (entry.curatable_id === targetCuratableId || entry.id === updated.curated_item_id) {
            found = true;
            return patch(entry);
          }
          return entry;
        }

        // Section with children
        const children = entry.curated_items ?? [];
        const updatedChildren = children.map((child: any) => {
          if (child.curatable_id === targetCuratableId || child.id === updated.curated_item_id) {
            found = true;
            return patch(child);
          }
          return child;
        });

          return { ...entry, curated_items: updatedChildren };
        });

    };

    eventBus.on('external-link-added', addedHandler);
    eventBus.on('external-link-updated', updatedHandler);
    eventBus.on('question-set-added', addedHandler);

    return () => {
      eventBus.off('external-link-added', addedHandler);
      eventBus.off('external-link-updated', updatedHandler);
      eventBus.off('question-set-added', addedHandler);
    };
  });
</script>
{#if contentsJSON}
  <div bind:this={contentsList} data-id={curationId} class="flex flex-col gap-12">
    {#each contentsJSON as subModule (subModule.id)}
      {#if subModule.is_curation}
        <CurationSection
          {parentSortable}
          editable={editable}
          parentId={curationId}
          sectionContents={subModule.curated_items}
          sectionCuratableId={subModule.curatable_id}
          sectionDescription={subModule.description}
          sectionDuration={subModule.duration}
          sectionId={subModule.id}
          sectionTitle={subModule.title}
          sectionDescriptionPlaceholder={sectionDescriptionPlaceholder}
          sectionDragPlaceholder={sectionDragPlaceholder}
          sectionDurationLabel={sectionDurationLabel}
          sectionTitlePlaceholder={sectionTitlePlaceholder}
          learningObjectivesHeader={learningObjectivesHeader}
          sectionActionsMoveUpLabel={sectionActionsMoveUpLabel}
          sectionActionsMoveDownLabel={sectionActionsMoveDownLabel}
          sectionActionsDeleteLabel={sectionActionsDeleteLabel}
          sectionDeleteConfirmationText={sectionDeleteConfirmationText}
          itemDeleteConfirmationText={itemDeleteConfirmationText}
          on:move={() => {
            // on move, refresh parent sortable so child index is updated
            parentSortable = parentSortable;
          }}
        />
      {:else if subModule.curatable_type === 'QuestionSet'}
        <!-- Hidden from learners until they can answer question sets (BIG-401). -->
        {#if editable}
          <CurationQuestionSet
            id={subModule.id}
            curationId={subModule.curation_id}
            questionSetId={subModule.curatable_id}
            title={subModule.title}
            description={subModule.description}
            durationSeconds={subModule.duration_seconds}
            initialQuestions={subModule.questions ?? []}
            editable={editable}
            addOptionText={questionSetAddOptionText}
            addQuestionText={questionSetAddQuestionText}
            bestPracticesText={questionSetBestPracticesText}
            deleteLabel={questionSetDeleteLabel}
            deleteOptionConfirmText={questionSetDeleteOptionConfirmText}
            deleteOptionLabel={questionSetDeleteOptionLabel}
            deleteQuestionLabel={questionSetDeleteQuestionLabel}
            descriptionPlaceholder={questionSetDescriptionPlaceholder}
            duplicateLabel={questionSetDuplicateLabel}
            duplicateQuestionLabel={questionSetDuplicateQuestionLabel}
            durationLabel={sectionDurationLabel}
            durationPlaceholder={questionSetDurationPlaceholder}
            emptyStateText={questionSetEmptyStateText}
            helpCenterLinkText={questionSetHelpCenterLinkText}
            minutesText={questionSetMinutesText}
            moveDownLabel={questionSetMoveDownLabel}
            moveLabel={questionSetMoveLabel}
            moveQuestionLabel={questionSetMoveQuestionLabel}
            moveToBottomLabel={questionSetMoveToBottomLabel}
            moveToTopLabel={questionSetMoveToTopLabel}
            moveUpLabel={questionSetMoveUpLabel}
            multipleChoiceText={questionSetMultipleChoiceText}
            openEndedText={questionSetOpenEndedText}
            optionPlaceholder={questionSetOptionPlaceholder}
            questionPlaceholder={questionSetQuestionPlaceholder}
            responsePreviewText={questionSetResponsePreviewText}
            saveAndCloseText={questionSetSaveAndCloseText}
            savedText={questionSetSavedText}
            titlePlaceholder={questionSetTitlePlaceholder}
          />
        {/if}
      {:else}
        <CurationListItem
          addModalText={addModalText}
          addToCurationText={addToCurationText}
          contentTypeText={contentTypeText}
          curatableType={subModule.curatable_type}
          curatableId={subModule.curatable_id}
          descriptionLabel={descriptionLabel}
          dropdownArticle={dropdownArticle}
          dropdownAudio={dropdownAudio}
          dropdownLink={dropdownLink}
          dropdownSurvey={dropdownSurvey}
          dropdownText={dropdownText}
          dropdownVideo={dropdownVideo}
          duration={subModule.duration}
          editable={editable}
          estimatedDurationLabel={estimatedDurationLabel}
          editModalText={editModalText}
          externalUrl={subModule.external_url}
          href={subModule.href}
          id={subModule.id}
          is_nonexpert_path={subModule.is_nonexpert_path}
          itemDeleteConfirmationText={itemDeleteConfirmationText}
          learning_objectives={subModule.learning_objectives}
          learningObjectivesHeader={learningObjectivesHeader}
          linkText={linkText}
          optionalText={optionalText}
          parent={subModule.curation_id}
          pasteUrlHereText={pasteUrlHereText}
          path_color={subModule.path_color}
          path_icon={subModule.path_icon}
          path_image={subModule.path_image}
          progress={subModule.progress}
          requiredText={requiredText}
          saveChangesText={saveChangesText}
          subhead={subModule.expert_name}
          subeyebrow={subModule.expert_title}
          thumbnail_url={subModule.thumbnail_url}
          title={subModule.title}
          titleLabel={titleLabel}
          titlePlaceholder={titlePlaceholder}
          type_overlay={subModule.type_overlay}
          updateable={subModule.updateable}
          validUrlText={validUrlText}
        />
      {/if}
    {/each}
  </div>
{:else}
  <div class="w-full max-w-xl mx-auto">
    <div
      class="bg-neutral-300 border border-neutral-400 aspect-video w-full rounded overflow-hidden"
    >
      <img class="w-full h-full object-cover" src="/img/demo.png" alt="" />
    </div>
  </div>
{/if}
